import axios from 'axios'
import {Listeners} from '../constants/listeners'

/**
 * Жизненный цикл фоновой выгрузки: queued → processing → done | error.
 *
 * Раньше это жило в page/aianswers/AiAnswers.js и работало только для одного отчёта.
 * Теперь лежит в KSTable, поэтому фоновую выгрузку получают все таблицы,
 * не трогая twig-шаблоны каждого отчёта.
 */
export default {
    data() {
        return {
            csvExportJobStopped: false,
        }
    },
    beforeUnmount() {
        this.csvExportJobStopped = true;
    },
    computed: {
        /**
         * Общая ручка статуса. Компонент может переопределить её своей — у отчёта
         * с собственной очередью (например ai-answers) адрес другой.
         */
        apiCsvExportStatusUrl() {
            return this.$config.API_URL + '/report/export-status';
        },
    },
    methods: {
        /**
         * Ключ привязан к странице вместе с query: домен и база лежат именно там,
         * иначе на том же отчёте по другому домену подхватилось бы чужое задание.
         */
        csvExportStorageKey() {
            return `ks_csv_export_job_v1:${window.location.pathname}${window.location.search}`;
        },
        saveCsvExportJobId(jobId) {
            if (!jobId) {
                return;
            }
            try {
                localStorage.setItem(this.csvExportStorageKey(), JSON.stringify({
                    jobId: String(jobId),
                    savedAt: Date.now(),
                }));
            } catch (e) {}
        },
        getSavedCsvExportJobId() {
            try {
                const raw = localStorage.getItem(this.csvExportStorageKey());
                if (!raw) {
                    return null;
                }
                const data = JSON.parse(raw);
                const savedAt = Number(data?.savedAt || 0);
                const ttlMs = 24 * 60 * 60 * 1000;
                if (!data?.jobId || !savedAt || (Date.now() - savedAt) > ttlMs) {
                    this.clearSavedCsvExportJobId();
                    return null;
                }
                return String(data.jobId);
            } catch (e) {
                this.clearSavedCsvExportJobId();
                return null;
            }
        },
        clearSavedCsvExportJobId() {
            try {
                localStorage.removeItem(this.csvExportStorageKey());
            } catch (e) {}
        },
        emitCsvProgress(data = {}) {
            this.$emitter.emit(Listeners.KS_TABLE_CSV_EXPORT_PROGRESS, {status: data?.status});
        },
        emitCsvFinished() {
            this.$emitter.emit(Listeners.KS_TABLE_CSV_EXPORT_FINISHED, {});
        },
        openCsvDownload(url) {
            if (url) {
                window.location.href = url;
            }
        },
        /**
         * Если пользователь перезагрузил страницу, продолжаем следить за уже запущенной выгрузкой.
         */
        async resumeCsvExportIfNeeded() {
            const savedJobId = this.getSavedCsvExportJobId();
            if (!savedJobId) {
                return;
            }

            this.emitCsvProgress({status: 'queued'});
            await this.waitForCsvExport(savedJobId);
        },
        async waitForCsvExport(jobId) {
            try {
                const status = await this.pollCsvExportStatus(jobId);
                if (this.csvExportJobStopped) {
                    return;
                }

                this.clearSavedCsvExportJobId();

                if (status?.status === 'done' && status?.downloadUrl) {
                    this.openCsvDownload(status.downloadUrl);
                    this.alertSuccess(this.$t('lang.main.download_file'));
                } else {
                    this.alertError(status?.error || status?.message || this.$t('lang.main.toast.error_title'));
                }
            } catch (error) {
                if (this.csvExportJobStopped) {
                    return;
                }
                this.clearSavedCsvExportJobId();
                this.alertError(error?.response?.data?.message || this.$t('lang.main.toast.error_title'));
            } finally {
                this.emitCsvFinished();
            }
        },
        /**
         * Опрашивает статус до терминального. Верхняя граница ожидания — примерно 14 минут:
         * выгрузки длиннее этого в замерах не встречались.
         */
        async pollCsvExportStatus(jobId) {
            const maxAttempts = 120;
            const intervalMs = 7000;

            for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                if (this.csvExportJobStopped) {
                    return {status: 'error'};
                }

                const response = await axios({
                    method: 'get',
                    url: this.apiCsvExportStatusUrl,
                    withCredentials: true,
                    params: {job_id: jobId},
                });

                const status = response?.data || {};
                this.emitCsvProgress(status);

                if (['done', 'error', 'not_found'].includes(status.status)) {
                    return status;
                }

                await new Promise((resolve) => setTimeout(resolve, intervalMs));
            }

            return {status: 'error', message: this.$t('lang.main.toast.error_title')};
        },
    },
}
